import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = 'https://api.botanix.in/api';
const ANTHROPIC_API = 'https://api.botanix.in/api/notify/ai-chat';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#0F2040',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#22C55E', blue:'#FFFFFF',
 red:'#EF4444', redBg:'rgba(239,68,68,.10)',
 amber:'#F59E0B', amberBg:'rgba(245,158,11,.10)',
 purple:'#A78BFA', gold:'#D4A017',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});
const todayStr = new Date().toISOString().slice(0,10);

const QUICK_ACTIONS = [
 { label:"Today's arrivals?", query:"How many guests are arriving today and what are their names?" },
 { label:'Pending payments?', query:"Which bookings have pending balance payments? Give me a list." },
 { label:'Kitchen meal count?', query:"What is the total meal count for today across all bookings?" },
 { label:'Occupancy summary?', query:"What is today's occupancy percentage and room status summary?" },
 { label:'Wedding details today?', query:"Tell me about any wedding bookings happening today or this week." },
 { label:'Corporate groups today?', query:"List all corporate groups arriving or in-house today." },
 { label:'Revenue this month?', query:"What is the total revenue booked for this month?" },
 { label:'Draft payment reminder', query:"Draft a professional WhatsApp message to remind a guest about their pending payment." },
 { label:'Suggest weekend offer', query:"Suggest a weekend promotional offer for Botanix Nature Resort to boost occupancy." },
 { label:'Menu suggestion', query:"Suggest a menu for today based on the bookings we have — wedding, corporate and room stay guests." },
 { label:'Draft booking confirmation', query:"Draft a professional booking confirmation message for a wedding booking at Botanix Nature Resort." },
 { label:'Maintenance priorities', query:"What should be the maintenance priorities for today at the resort?" },
];

const SYSTEM_PROMPT = (bookingData) => `You are Botanix AI, the intelligent assistant for Botanix Nature Resort, Village Damdama, Tehsil Sohna, Gurgaon-122102, Haryana, India.

You are embedded inside the Botanix ERP system and have access to live booking data.

RESORT INFORMATION:
- Name: Botanix Nature Resort (Botanix Resort Pvt. Ltd.)
- Location: Village Damdama, Tehsil Sohna, Gurgaon-122102
- Phone: +91-9211445430
- Email: info@botanix.in
- GST: 06AAGCB9754R1ZT
- Bank: ICICI Bank, A/C: 031405004054, IFSC: ICIC0000314
- Rooms: Mix of Forest View, Lakeside Suite, Canopy Cottage, Jungle Tent, Driver Rooms
- Specialties: Weddings, Corporate Day Outings, Day Picnics, Nature Activities

LIVE BOOKING DATA (Today: ${todayStr}):
Total Bookings: ${bookingData.total}
Today's Arrivals: ${bookingData.todayArrivals.length} bookings
Today's Day Picnic: ${bookingData.dayPicnic.length} groups (${bookingData.dayPicnicPax} pax)
Today's Weddings: ${bookingData.weddings.length} events (${bookingData.weddingPax} pax)
Today's Corporate: ${bookingData.corporate.length} groups
Pending Payments: ${bookingData.pendingPayments} bookings with balance due
Total Revenue (all bookings): ${inr(bookingData.totalRevenue)}

RECENT BOOKINGS:
${bookingData.recent.map(b => `- ${b.booking_code}: ${b.guest_name||b.company_name||'Guest'} | ${b.booking_type} | ${b.pax_count} pax | ${b.start_date?.slice(0,10)} | ${b.booking_status} | Total: ${inr(b.total_amount)} | Balance: ${inr(b.balance_amount)}`).join('\n')}

YOUR ROLE:
- Answer questions about bookings, revenue, guests, operations
- Draft professional emails, WhatsApp messages, confirmations
- Suggest promotional offers, menu ideas, operational improvements
- Provide insights on occupancy, revenue, food cost
- Help staff with their department-specific queries
- Be concise, professional and helpful
- Always use Indian context (₹ for currency, Indian hospitality norms)
- For drafts, provide ready-to-use text that can be copied directly

Keep responses concise and actionable. Use bullet points for lists. Use ₹ for all amounts.`;

export default function BotanixAI() {
 const [messages, setMessages] = useState([]);
 const [input, setInput] = useState('');
 const [loading, setLoading] = useState(false);
 const [bookingData, setBookingData] = useState(null);
 const [loadingData, setLoadingData] = useState(true);
 const [tab, setTab] = useState('chat'); // chat | analytics
 const messagesEndRef = useRef(null);

 const currentUser = (() => { try { return JSON.parse(localStorage.getItem('botanix_user')); } catch { return null; } })();

 // Load booking data
 const loadData = useCallback(async () => {
 setLoadingData(true);
 try {
 const [b, inv] = await Promise.all([
 fetch(`${API}/bookings`).then(r=>r.json()),
 fetch(`${API}/accounts/invoices`).then(r=>r.json()),
 ]);
 const bookings = Array.isArray(b) ? b : [];
 const invoices = Array.isArray(inv) ? inv : [];

 const todayArrivals = bookings.filter(b=>b.start_date?.slice(0,10)===todayStr&&!['Cancelled','No Show'].includes(b.booking_status));
 const dayPicnic = bookings.filter(b=>b.booking_type?.includes('Picnic')&&b.start_date?.slice(0,10)===todayStr);
 const weddings = bookings.filter(b=>b.booking_type?.includes('Wedding')&&b.start_date?.slice(0,10)===todayStr);
 const corporate = bookings.filter(b=>b.booking_type?.includes('Corporate')&&b.start_date?.slice(0,10)===todayStr);
 const pending = bookings.filter(b=>parseFloat(b.balance_amount||0)>0&&b.booking_status!=='Cancelled');
 const totalRevenue = bookings.filter(b=>b.booking_status!=='Cancelled').reduce((s,b)=>s+parseFloat(b.total_amount||0),0);

 setBookingData({
 total: bookings.length,
 todayArrivals,
 dayPicnic,
 dayPicnicPax: dayPicnic.reduce((s,b)=>s+parseInt(b.pax_count||0),0),
 weddings,
 weddingPax: weddings.reduce((s,b)=>s+parseInt(b.pax_count||0),0),
 corporate,
 pendingPayments: pending.length,
 totalRevenue,
 recent: bookings.slice(-10).reverse(),
 invoices,
 });
 } catch(e) { console.error(e); }
 finally { setLoadingData(false); }
 }, []);

 useEffect(() => { loadData(); }, [loadData]);

 useEffect(() => {
 messagesEndRef.current?.scrollIntoView({ behavior:'smooth' });
 }, [messages]);

 // Welcome message
 useEffect(() => {
 setMessages([{
 role: 'assistant',
 content: ` **Welcome to Botanix AI, ${currentUser?.name?.split(' ')[0] || 'there'}!**\n\nI'm your intelligent assistant for Botanix Nature Resort. I have access to your live booking data and can help you with:\n\n• **Booking queries** — arrivals, departures, guest details\n• **Revenue & payments** — outstanding balances, monthly revenue\n• **Operations** — kitchen meal counts, housekeeping, maintenance\n• **Drafts** — emails, WhatsApp messages, confirmations\n• **Suggestions** — offers, menu ideas, operational tips\n\nWhat would you like to know?`,
 time: new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}),
 }]);
 }, []);

 const sendMessage = async (text) => {
 const userMsg = text || input.trim();
 if (!userMsg || loading || !bookingData) return;
 setInput('');

 const newMessages = [...messages, {
 role:'user', content:userMsg,
 time: new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}),
 }];
 setMessages(newMessages);
 setLoading(true);

 try {
 const apiMessages = newMessages
 .filter(m=>m.role==='user'||m.role==='assistant')
 .map(m=>({ role:m.role, content:m.content }));

 const res = await fetch(ANTHROPIC_API, {
 method:'POST',
 headers:{ 'Content-Type':'application/json' },
 body: JSON.stringify({
 model: 'claude-sonnet-4-6',
 max_tokens: 1000,
 system: SYSTEM_PROMPT(bookingData),
 messages: apiMessages,
 }),
 });

 const data = await res.json();
 const reply = data.content?.[0]?.text || 'Sorry, I could not process that request.';

 setMessages(p => [...p, {
 role:'assistant', content:reply,
 time: new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}),
 }]);
 } catch(e) {
 setMessages(p => [...p, {
 role:'assistant', content:' Connection error. Please try again.',
 time: new Date().toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'}),
 }]);
 }
 finally { setLoading(false); }
 };

 // Format message with markdown-like styling
 const formatMsg = (text) => {
 return text
 .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
 .replace(/\*(.*?)\*/g, '<em>$1</em>')
 .replace(/\n/g, '<br/>');
 };

 // Analytics data
 const analytics = bookingData ? [
 { label:'Total Bookings', value:bookingData.total, color:C.accent, trend:'+12% vs last month' },
 { label:'Today\'s Arrivals', value:bookingData.todayArrivals.length, color:C.accent, trend:'Check-ins due today' },
 { label:'Day Picnic Pax', value:bookingData.dayPicnicPax, color:'#FFFFFF', trend:'Today\'s groups' },
 { label:'Wedding Guests', value:bookingData.weddingPax, color:'#FFFFFF', trend:'Today\'s events' },
 { label:'Pending Payments', value:bookingData.pendingPayments, color:C.red, trend:'Balances outstanding' },
 { label:'Total Revenue', value:inr(bookingData.totalRevenue), color:C.amber, icon:'₹', trend:'All confirmed bookings' },
 ] : [];

 const inp2 = { flex:1, padding:'12px 16px', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border2}`, borderRadius:12, color:C.text, fontSize:14, outline:'none', fontFamily:'inherit' };

 return (
 <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:C.bg, color:C.text, fontFamily:'Inter,sans-serif' }}>

 {/* Header */}
 <div style={{ padding:'14px 24px', background:C.surface, borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
 <div style={{ display:'flex', alignItems:'center', gap:12 }}>
 <div style={{ width:40, height:40, borderRadius:12, background:`linear-gradient(135deg,${C.accent},#16A34A)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}></div>
 <div>
 <div style={{ fontWeight:800, fontSize:16, color:C.text }}>Botanix AI Assistant</div>
 <div style={{ fontSize:11, color:C.accent, marginTop:1 }}>
 {loadingData ? ' Loading live data…' : ` Connected · ${bookingData?.total} bookings loaded`}
 </div>
 </div>
 </div>
 <div style={{ display:'flex', gap:8, alignItems:'center' }}>
 {/* Tab switcher */}
 <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,.05)', borderRadius:8, padding:3 }}>
 {[{id:'chat',label:' Chat'},{id:'analytics',label:' Analytics'}].map(t=>(
 <button key={t.id} onClick={()=>setTab(t.id)}
 style={{ padding:'6px 14px', borderRadius:6, border:'none', cursor:'pointer', fontSize:12, fontWeight:600, background:tab===t.id?C.accent:'transparent', color:tab===t.id?'#fff':C.text2 }}>
 {t.label}
 </button>
 ))}
 </div>
 <button onClick={loadData} style={{ padding:'7px 14px', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:12 }}></button>
 </div>
 </div>

 {/* Analytics Tab */}
 {tab==='analytics' && (
 <div style={{ flex:1, overflowY:'auto', padding:24 }}>
 <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:6 }}> AI Analytics Dashboard</div>
 <div style={{ fontSize:13, color:C.text2, marginBottom:20 }}>Live insights powered by your booking data</div>

 {/* KPI Cards */}
 <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(180px,1fr))', gap:14, marginBottom:24 }}>
 {analytics.map(a=>(
 <div key={a.label} style={{ background:C.surface, border:`1px solid ${a.color}33`, borderRadius:14, padding:18 }}>
 <div style={{ fontSize:24, marginBottom:8 }}>{a.icon}</div>
 <div style={{ fontSize:28, fontWeight:900, color:a.color, marginBottom:4 }}>{a.value}</div>
 <div style={{ fontSize:12, color:C.text2, marginBottom:4 }}>{a.label}</div>
 <div style={{ fontSize:11, color:C.text3 }}>{a.trend}</div>
 </div>
 ))}
 </div>

 {/* AI Suggestions */}
 <div style={{ fontWeight:700, fontSize:16, color:C.text, marginBottom:14 }}> AI Suggestions</div>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:24 }}>
 {[
 { trigger:'Analyse occupancy and suggest action', color:C.red, priority:'High' },
 { trigger:'Review pending payments and draft reminders', color:C.amber, priority:'High' },
 { trigger:'Suggest weekend offer based on bookings', color:C.accent, priority:'Medium' },
 { trigger:'Generate today\'s kitchen meal forecast', color:C.amber, priority:'Medium' },
 { trigger:'Wedding event checklist for today', color:C.purple, priority:'High' },
 { trigger:'Draft follow-up for tentative bookings', color:'#FFFFFF', priority:'Low' },
 ].map((s,i)=>(
 <div key={i} style={{ background:C.surface, border:`1px solid ${s.color}33`, borderRadius:12, padding:14, cursor:'pointer' }}
 onClick={()=>{ setTab('chat'); setTimeout(()=>sendMessage(s.trigger),100); }}>
 <div style={{ fontSize:22, marginBottom:8 }}>{s.icon}</div>
 <div style={{ fontSize:13, color:C.text, marginBottom:8 }}>{s.trigger}</div>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <span style={{ padding:'2px 8px', borderRadius:4, fontSize:10, fontWeight:700, background:`${s.color}20`, color:s.color }}>{s.priority}</span>
 <span style={{ fontSize:11, color:C.text3 }}>Click to ask AI →</span>
 </div>
 </div>
 ))}
 </div>

 {/* Booking breakdown */}
 {bookingData && (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:18 }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:14 }}> Recent Bookings</div>
 <div style={{ overflowX:'auto' }}>
 <table style={{ width:'100%', borderCollapse:'collapse' }}>
 <thead>
 <tr style={{ borderBottom:`1px solid ${C.border}` }}>
 {['Code','Guest','Type','Date','Pax','Amount','Status'].map(h=>(
 <th key={h} style={{ padding:'8px 12px', fontSize:11, color:C.text2, fontWeight:700, textAlign:'left', textTransform:'uppercase' }}>{h}</th>
 ))}
 </tr>
 </thead>
 <tbody>
 {bookingData.recent.slice(0,8).map((b,i)=>(
 <tr key={b.id} style={{ borderBottom:`1px solid rgba(30,58,95,.5)` }}>
 <td style={{ padding:'8px 12px', fontSize:12, fontWeight:700, color:C.accent }}>{b.booking_code}</td>
 <td style={{ padding:'8px 12px', fontSize:12, color:C.text }}>{b.guest_name||b.company_name||'—'}</td>
 <td style={{ padding:'8px 12px', fontSize:11, color:C.text2 }}>{b.booking_type}</td>
 <td style={{ padding:'8px 12px', fontSize:11, color:C.text2 }}>{b.start_date?.slice(0,10)}</td>
 <td style={{ padding:'8px 12px', fontSize:12, color:C.text, textAlign:'center' }}>{b.pax_count}</td>
 <td style={{ padding:'8px 12px', fontSize:12, fontWeight:700, color:C.amber }}>{inr(b.total_amount)}</td>
 <td style={{ padding:'8px 12px' }}>
 <span style={{ padding:'2px 8px', borderRadius:99, fontSize:10, fontWeight:700,
 background:b.booking_status==='Confirmed'?`${C.accent}20`:b.booking_status==='Tentative'?`${C.amber}20`:`${C.red}20`,
 color:b.booking_status==='Confirmed'?C.accent:b.booking_status==='Tentative'?C.amber:C.red }}>
 {b.booking_status}
 </span>
 </td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 </div>
 )}
 </div>
 )}

 {/* Chat Tab */}
 {tab==='chat' && (
 <>
 {/* Quick actions */}
 <div style={{ padding:'12px 20px', borderBottom:`1px solid ${C.border}`, flexShrink:0, overflowX:'auto' }}>
 <div style={{ display:'flex', gap:8, whiteSpace:'nowrap' }}>
 {QUICK_ACTIONS.map((a,i)=>(
 <button key={i} onClick={()=>sendMessage(a.query)}
 style={{ padding:'6px 12px', background:'rgba(34,197,94,.1)', border:'1px solid rgba(34,197,94,.3)', borderRadius:20, color:C.accent, cursor:'pointer', fontSize:12, fontWeight:600, whiteSpace:'nowrap', flexShrink:0 }}>
 {a.icon} {a.label}
 </button>
 ))}
 </div>
 </div>

 {/* Messages */}
 <div style={{ flex:1, overflowY:'auto', padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>
 {messages.map((m,i)=>(
 <div key={i} style={{ display:'flex', gap:12, alignItems:'flex-start', flexDirection:m.role==='user'?'row-reverse':'row' }}>
 {/* Avatar */}
 <div style={{ width:36, height:36, borderRadius:10, background:m.role==='user'?`linear-gradient(135deg,${C.accent},#16A34A)`:'linear-gradient(135deg,#1E3A5F,#0F2040)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>
 {m.role==='user' ? (currentUser?.name?.[0]||'U') : ''}
 </div>
 {/* Bubble */}
 <div style={{ maxWidth:'75%', background:m.role==='user'?`rgba(34,197,94,.15)`:C.surface, border:`1px solid ${m.role==='user'?'rgba(34,197,94,.3)':C.border}`, borderRadius:m.role==='user'?'16px 4px 16px 16px':'4px 16px 16px 16px', padding:'12px 16px' }}>
 <div style={{ fontSize:13, color:C.text, lineHeight:1.7 }} dangerouslySetInnerHTML={{ __html:formatMsg(m.content) }} />
 <div style={{ fontSize:10, color:C.text3, marginTop:6, textAlign:m.role==='user'?'right':'left' }}>{m.time}</div>
 {/* Copy button for assistant messages */}
 {m.role==='assistant' && (
 <button onClick={()=>navigator.clipboard?.writeText(m.content)}
 style={{ marginTop:6, padding:'3px 8px', background:'rgba(255,255,255,.05)', border:`1px solid ${C.border}`, borderRadius:4, color:C.text3, cursor:'pointer', fontSize:10 }}>
 Copy
 </button>
 )}
 </div>
 </div>
 ))}
 {loading && (
 <div style={{ display:'flex', gap:12, alignItems:'flex-start' }}>
 <div style={{ width:36, height:36, borderRadius:10, background:'linear-gradient(135deg,#1E3A5F,#0F2040)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16 }}></div>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:'4px 16px 16px 16px', padding:'14px 18px' }}>
 <div style={{ display:'flex', gap:4, alignItems:'center' }}>
 {[0,1,2].map(i=>(
 <div key={i} style={{ width:8, height:8, borderRadius:'50%', background:C.accent, animation:`bounce 1.2s ${i*0.2}s infinite` }} />
 ))}
 <style>{`@keyframes bounce{0%,80%,100%{transform:scale(0)}40%{transform:scale(1)}}`}</style>
 </div>
 </div>
 </div>
 )}
 <div ref={messagesEndRef} />
 </div>

 {/* Input */}
 <div style={{ padding:'16px 20px', borderTop:`1px solid ${C.border}`, flexShrink:0 }}>
 <div style={{ display:'flex', gap:10, alignItems:'center' }}>
 <input style={inp2} type="text" value={input} onChange={e=>setInput(e.target.value)}
 onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMessage()}
 placeholder="Ask anything about bookings, revenue, guests, operations…"
 disabled={loading||loadingData} />
 <button onClick={()=>sendMessage()} disabled={loading||!input.trim()||loadingData}
 style={{ padding:'12px 20px', background:loading||!input.trim()?'rgba(34,197,94,.3)':`linear-gradient(135deg,${C.accent},#16A34A)`, border:'none', borderRadius:12, color:'#fff', cursor:loading||!input.trim()?'not-allowed':'pointer', fontSize:14, fontWeight:700, flexShrink:0 }}>
 {loading ? '' : '→'}
 </button>
 </div>
 <div style={{ fontSize:11, color:C.text3, marginTop:8, textAlign:'center' }}>
 Powered by Claude AI · Connected to live Botanix booking data
 </div>
 </div>
 </>
 )}
 </div>
 );
}
